import { useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import type { Product } from '@eprintsmart/shared';
import { ProductImageManager } from './ProductImageManager';

/**
 * The image manager as a dialog, so photos can be attached from the category
 * tree without navigating into the product editor and back — which is the whole
 * point when you are working through a category adding pictures.
 *
 * Closing is guarded rather than free: an upload that is still processing, or a
 * reorder that has not been saved, is silently lost otherwise. Nothing is
 * destroyed — an uploaded image is in the Media library regardless — so the
 * prompt says so instead of implying the file is gone.
 */
export function ProductImagesDialog({
  product,
  open,
  onClose,
  onSaved,
}: {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [state, setState] = useState({ dirty: false, pending: 0 });

  const handleClose = useCallback(
    (_e?: unknown, reason?: string) => {
      // Backdrop and Escape are too easy to hit while watching a progress bar.
      if (reason === 'backdropClick' && (state.dirty || state.pending > 0)) return;
      if (state.pending > 0 && !confirm(
        `${state.pending} image${state.pending === 1 ? ' is' : 's are'} still processing. Close anyway? ` +
        'They stay in the Media library and can be attached from there.',
      )) return;
      if (!state.pending && state.dirty && !confirm('Discard unsaved image changes?')) return;
      setState({ dirty: false, pending: 0 });
      onClose();
    },
    [state, onClose],
  );

  if (!product) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        Images
        <Typography variant="body2" color="text.secondary">{product.name}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <ProductImageManager
          productId={product.id}
          productName={product.name}
          initialImageIds={product.imageIds ?? []}
          onSaved={onSaved}
          onDirtyChange={setState}
        />
      </DialogContent>
    </Dialog>
  );
}
